import { Injectable } from '@angular/core';
import { ApiService } from './api.service';
import { SheetLoggerService } from './sheet-logger.service';

export interface HeardAboutUsValue {
  label: string;
  followUpPlaceholder: string;
}

export interface HeardAboutUsOption {
  label: string;
  textField: boolean;
  textPlaceholder: string;
  values: HeardAboutUsValue[];
}

interface HeardAboutUsOptionsCachePayload {
  options: HeardAboutUsOption[];
  createdAt: number;
}

@Injectable({ providedIn: 'root' })
export class HeardAboutUsOptionsService {
  private static readonly STORAGE_KEY = 'ols.heardAboutUsOptions';
  private static readonly MAX_AGE_MS = 24 * 60 * 60 * 1000;
  private static readonly MAX_ATTEMPTS = 2;
  private options: HeardAboutUsOption[] = [];
  private cachedAt = 0;
  private inflight: Promise<HeardAboutUsOption[]> | null = null;
  private fetchAttempted = false;

  constructor(private api: ApiService, private sheetLogger: SheetLoggerService) {}

  prefetch(forceRefresh = false): Promise<HeardAboutUsOption[]> {
    if (typeof window === 'undefined') return Promise.resolve([]);
    if (forceRefresh) {
      this.clearCache();
      this.fetchAttempted = false;
    }
    return this.ensureLoaded();
  }

  async ensureLoaded(): Promise<HeardAboutUsOption[]> {
    const existing = this.readCached();
    if (existing.length > 0) return existing;
    if (this.inflight) return this.inflight;
    if (this.fetchAttempted) return [];
    this.inflight = this.fetchWithRetries().finally(() => {
      this.fetchAttempted = true;
      this.inflight = null;
    });
    return this.inflight;
  }

  getOptions(): HeardAboutUsOption[] {
    return this.readCached();
  }

  getOption(label: string): HeardAboutUsOption | null {
    const wanted = (label || '').trim().toLowerCase();
    if (!wanted) return null;
    return this.getOptions().find((item) => (item.label || '').trim().toLowerCase() === wanted) || null;
  }

  private readCached(): HeardAboutUsOption[] {
    if (this.options.length > 0) {
      if (this.isFresh(this.cachedAt)) return this.options;
      this.clearCache();
      return [];
    }
    try {
      const raw = localStorage.getItem(HeardAboutUsOptionsService.STORAGE_KEY);
      if (!raw) return [];
      const parsed = JSON.parse(raw) as HeardAboutUsOptionsCachePayload;
      if (!this.isFresh(parsed?.createdAt) || !Array.isArray(parsed?.options) || !parsed.options.length) {
        this.clearCache();
        return [];
      }
      this.options = parsed.options;
      this.cachedAt = Number(parsed.createdAt) || Date.now();
      return this.options;
    } catch {
      this.clearCache();
      return [];
    }
  }

  private async fetchWithRetries(): Promise<HeardAboutUsOption[]> {
    for (let attempt = 1; attempt <= HeardAboutUsOptionsService.MAX_ATTEMPTS; attempt++) {
      const options = await this.fetchOnce();
      if (options.length > 0) {
        this.store(options);
        return options;
      }
      if (attempt < HeardAboutUsOptionsService.MAX_ATTEMPTS) {
        await this.delay(300 * attempt);
      }
    }
    return this.readCached();
  }

  private fetchOnce(): Promise<HeardAboutUsOption[]> {
    return new Promise((resolve) => {
      const url = this.sheetLogger.getHearAboutUsOptionsWebhookUrl();
      const payload: Record<string, any> = {};
      if (url) payload['url'] = url;
      this.api.dispatch<{ options?: any[] }>('/heard_about_us/options', payload).subscribe({
        next: (resp) => resolve(this.normalize(resp?.options)),
        error: () => resolve([])
      });
    });
  }

  private normalize(raw: any[] | null | undefined): HeardAboutUsOption[] {
    const list = Array.isArray(raw) ? raw : [];
    const options: HeardAboutUsOption[] = [];
    for (const item of list) {
      const label = String(item?.label || '').trim();
      if (!label) continue;
      const values: HeardAboutUsValue[] = [];
      for (const value of Array.isArray(item?.values) ? item.values : []) {
        const valueLabel = String(value?.label || '').trim();
        if (!valueLabel) continue;
        values.push({
          label: valueLabel,
          followUpPlaceholder: String(value?.followUpPlaceholder || '').trim()
        });
      }
      options.push({
        label,
        textField: !!item?.textField,
        textPlaceholder: String(item?.textPlaceholder || '').trim(),
        values
      });
    }
    return options;
  }

  private store(options: HeardAboutUsOption[]): void {
    if (!options.length) return;
    this.options = options;
    this.cachedAt = Date.now();
    try {
      const payload: HeardAboutUsOptionsCachePayload = { options, createdAt: this.cachedAt };
      localStorage.setItem(HeardAboutUsOptionsService.STORAGE_KEY, JSON.stringify(payload));
    } catch {}
  }

  private clearCache(): void {
    this.options = [];
    this.cachedAt = 0;
    try { localStorage.removeItem(HeardAboutUsOptionsService.STORAGE_KEY); } catch {}
  }

  private isFresh(createdAt: number | undefined): boolean {
    const ts = Number(createdAt || 0);
    return Number.isFinite(ts) && ts > 0 && (Date.now() - ts) < HeardAboutUsOptionsService.MAX_AGE_MS;
  }

  private delay(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }
}
